import React, { useEffect, useRef } from 'react';
import AirDatepicker from 'air-datepicker';
import 'air-datepicker/air-datepicker.css';
import './AirDatePicker.css';
import localeEn from 'air-datepicker/locale/en';

/**
 * AirDatePicker — standardized date input for the entire application.
 *
 * Display format : dd-MM-yyyy  (shown to user)
 * Placeholder    : dd-mm-yyyy
 * onChange payload: 'yyyy-mm-dd' (string for backward compatibility)
 *
 * Incoming `value` prop must be a yyyy-mm-dd string (or empty).
 */
const AirDatePicker = React.forwardRef(
    ({ value, onChange, placeholder, className, style, id, onKeyDown, name, disabled, required }, ref) => {
        const inputRef = useRef(null);
        const dpRef = useRef(null);

        // Expose the inner input node via the forwarded ref
        React.useImperativeHandle(ref, () => inputRef.current);

        // --- Helpers ---
        const toYMD = (date) => {
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };

        // Initialize the datepicker once
        useEffect(() => {
            if (!inputRef.current) return;

            dpRef.current = new AirDatepicker(inputRef.current, {
                locale: localeEn,
                autoClose: true,
                dateFormat: 'dd-MM-yyyy',   // Display: dd-MM-yyyy
                onSelect({ date }) {
                    if (onChange) {
                        if (date) {
                            const d = Array.isArray(date) ? date[0] : date;
                            // 4-digit year guard
                            if (d.getFullYear() < 1000) return;
                            onChange(toYMD(d));
                        } else {
                            onChange('');
                        }
                    }
                }
            });

            return () => {
                if (dpRef.current) {
                    dpRef.current.destroy();
                    dpRef.current = null;
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Sync the displayed date when `value` prop changes externally
        useEffect(() => {
            if (!dpRef.current) return;

            if (value && typeof value === 'string') {
                let newDate;
                if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
                    // Parse yyyy-mm-dd without timezone offset issues
                    const parts = value.split('-');
                    newDate = new Date(
                        parseInt(parts[0], 10),
                        parseInt(parts[1], 10) - 1,
                        parseInt(parts[2].substring(0, 2), 10)
                    );
                } else {
                    newDate = new Date(value);
                }

                if (!isNaN(newDate.getTime()) && newDate.getFullYear() >= 1000) {
                    const current = dpRef.current.selectedDates[0];
                    if (!current || current.getTime() !== newDate.getTime()) {
                        dpRef.current.selectDate(newDate, { silent: true });
                    }
                }
            } else if (!value) {
                dpRef.current.clear();
            }
        }, [value]);

        // --- Input masking (dd-MM-yyyy) ---
        const handleInput = (e) => {
            // Strip non-digits
            let digits = e.target.value.replace(/\D/g, '');
            if (digits.length > 8) digits = digits.substring(0, 8);

            // Rebuild with '-' separators: dd-MM-yyyy
            let formatted = digits;
            if (digits.length > 2) {
                formatted = digits.substring(0, 2) + '-' + digits.substring(2);
            }
            if (digits.length > 4) {
                formatted = formatted.substring(0, 5) + '-' + digits.substring(4);
            }

            e.target.value = formatted;

            // When all 8 digits are entered, validate and select date
            if (digits.length === 8) {
                const day   = parseInt(digits.substring(0, 2), 10);
                const month = parseInt(digits.substring(2, 4), 10) - 1;
                const year  = parseInt(digits.substring(4, 8), 10);

                // 4-digit year validation
                if (year < 1000 || year > 9999) return;

                const parsed = new Date(year, month, day);
                if (
                    !isNaN(parsed.getTime()) &&
                    parsed.getDate() === day &&
                    parsed.getMonth() === month &&
                    parsed.getFullYear() === year &&
                    dpRef.current
                ) {
                    dpRef.current.selectDate(parsed);
                }
            } else if (digits.length === 0) {
                if (dpRef.current) {
                    dpRef.current.clear();
                    if (onChange) onChange('');
                }
            }
        };

        const handleInternalKeyDown = (e) => {
            if (
                e.key === 'Backspace' ||
                e.key === 'Delete'    ||
                e.key === 'ArrowLeft' ||
                e.key === 'ArrowRight'||
                e.key === 'Tab'       ||
                e.key === 'Enter'     ||
                e.ctrlKey || e.metaKey
            ) {
                if (onKeyDown) onKeyDown(e);
                return;
            }

            // Allow only digit keys
            if (!/^\d$/.test(e.key)) {
                e.preventDefault();
            } else {
                if (onKeyDown) onKeyDown(e);
            }
        };

        return (
            <input
                id={id}
                name={name}
                ref={inputRef}
                type="text"
                placeholder="dd-mm-yyyy"
                className={className}
                style={style}
                onInput={handleInput}
                onKeyDown={handleInternalKeyDown}
                disabled={disabled}
                required={required}
                autoComplete="off"
            />
        );
    }
);

AirDatePicker.displayName = 'AirDatePicker';

export default AirDatePicker;
